import { collection } from 'infra/collection'
import {
  Bundle as BundleProto,
  WorkspaceWithBundles as WorkspaceWithBundlesProto,
  CreateBundleRequest,
  ListBundlesRequest,
  GetWorkspacesWithBundlesRequest,
  EditBundleRequest,
  BundleUpdateOptions,
  ArchiveBundleRequest,
  UnarchiveBundleRequest,
} from '@taxfyle/api-internal/internal/bundle_pb'
import { Bundle } from './bundle'
import { PrimeUser } from 'iam/UserStore'
import WorkspaceStore, { PrimeWorkspace } from 'iam/WorkspaceStore'
import { CreateBundleParams, EditBundleParams } from './dtos/dtos'
import { numberToDecimal } from 'utils/grpc-util'
import { BundleAPI } from './API'
import WorkspaceWithBundles from './WorkspaceWithBundles'
import { Id } from 'types/definitions'
import { mapIncludedItemsIdsToProto } from './dtos/bundleGrpcDtoMapper'
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb'

export class BundleStore {
  bundles = collection<BundleProto.AsObject, Bundle>({
    create: () => new Bundle(this.primeUser, this.primeWorkspace),
  })

  workspacesWithBundles = collection<
    WorkspaceWithBundlesProto.AsObject,
    WorkspaceWithBundles
  >({
    create: () => new WorkspaceWithBundles(this.workspaceStore),
    getModelId: (model) => model.workspaceId,
    getDataId: (data) => data.workspaceId,
  })

  constructor(
    private bundleAPI: BundleAPI,
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
    private workspaceStore: WorkspaceStore,
  ) {}

  bundlesForWorkspace(workspaceId: Id): Bundle[] {
    return this.bundles.filter((bundle) => bundle.workspace.id === workspaceId)
  }

  allBundles(): Bundle[] {
    return this.bundles.items
  }

  async create(payload: CreateBundleParams) {
    const request = new CreateBundleRequest()
      .setName(payload.name)
      .setDescription(payload.description)
      .setPrice(numberToDecimal(payload.price))
      .setWorkspaceId(payload.workspaceId)
      .setBaseItemId(payload.baseItemId)
      .setIncludedItemsList(
        mapIncludedItemsIdsToProto(payload.includedItemsIds),
      )

    await this.bundleAPI.createBundle(request)
  }

  async fetchBundles(
    workspaceId: Id,
    limit: number,
    pageToken?: string,
    filter?: string,
    orderBy?: string,
  ) {
    const request = new ListBundlesRequest()

    if (workspaceId) {
      request.setWorkspaceId(workspaceId)
    }
    if (limit) {
      request.setPageSize(limit)
    }
    if (pageToken) {
      request.setPageToken(pageToken)
    }
    if (filter) {
      request.setFilter(filter)
    }
    if (orderBy) {
      request.setOrderBy(orderBy)
    }

    const response = await this.bundleAPI.listBundles(request)

    return {
      data: this.bundles.set(
        response.getBundlesList().map((bundle) => bundle.toObject()),
      ),
      cursor: response.getNextPageToken(),
    }
  }

  async getWorkspacesWithBundles() {
    const request = new GetWorkspacesWithBundlesRequest()

    const response = await this.bundleAPI.getWorkspacesWithBundles(request)

    this.workspacesWithBundles.reset(
      response.getWorkspacesList().map((w) => w.toObject()),
    )
  }

  async edit(id: string, payload: EditBundleParams) {
    const options = new BundleUpdateOptions()
      .setName(payload.name)
      .setDescription(payload.description)
      .setPrice(numberToDecimal(payload.price))
      .setBaseItemId(payload.baseItemId)
      .setIncludedItemsList(
        mapIncludedItemsIdsToProto(payload.includedItemsIds),
      )

    const fieldMask = new FieldMask().setPathsList([
      'name',
      'description',
      'price',
      'base_item_id',
      'included_items',
    ])

    const request = new EditBundleRequest()
      .setId(id)
      .setOptions(options)
      .setUpdateMask(fieldMask)

    await this.bundleAPI.editBundle(request)
  }

  async archive(id: string) {
    const request = new ArchiveBundleRequest().setId(id)

    await this.bundleAPI.archiveBundle(request)
  }

  async unarchive(id: string) {
    const request = new UnarchiveBundleRequest().setId(id)

    await this.bundleAPI.unarchiveBundle(request)
  }
}
